import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { EnhancedTokenManager } from "../../utils/tokenManager";
import { API_URLS, BASE_URL } from "../../api/urls";
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";

// Developer interface matching API response
interface Developer {
  email: string;
  name: string | null;
  role: string;
  profile_image: string | null;
}

// API Response interface
interface ApiResponse {
  success: boolean;
  card_id: number;
  developers: Developer[];
  workspace_owner_name: string;
  workspace_owner_profile: string | null;
}

// Combined User interface for rendering
interface User {
  email: string;
  name: string | null;
  profile_image: string | null;
  role: string; // "Owner" or "Developer"
  isOwner: boolean;
}

interface DeveloperAvatarsGroupProps {
  cardId: number;
}

// Helper to get full image URL
const getFullImageUrl = (imagePath: string | null): string | null => {
  if (!imagePath) return null;
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }
  const cleanPath = imagePath.replace(/\\/g, "/").replace(/^\/+/, "");
  return `${BASE_URL}/${cleanPath}`;
};

// Helper to get initials
const getInitials = (name: string | null, email: string): string => {
  if (name) {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return email.charAt(0).toUpperCase();
};

// Helper to get display name
const getDisplayName = (name: string | null, email: string): string => {
  return name || email.split("@")[0];
};

const DeveloperAvatarsGroup: React.FC<DeveloperAvatarsGroupProps> = ({
  cardId,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDevelopers = async () => {
      try {
        const accessToken = EnhancedTokenManager.getAccessToken();
        if (!accessToken) {
          setIsLoading(false);
          return;
        }

        const response = await fetch(
          API_URLS.GET_DEVELOPER_CARD_SENDERS_LIST(cardId),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data: ApiResponse = await response.json();

        if (data.success) {
          const allUsers: User[] = [];

          // Add workspace owner first
          if (data.workspace_owner_name) {
            allUsers.push({
              email: "", // Owner might not have email in response
              name: data.workspace_owner_name,
              profile_image: data.workspace_owner_profile,
              role: "Owner",
              isOwner: true,
            });
          }

          // Add developers
          if (data.developers && data.developers.length > 0) {
            data.developers.forEach((dev) => {
              allUsers.push({
                email: dev.email,
                name: dev.name,
                profile_image: dev.profile_image,
                role: dev.role,
                isOwner: false,
              });
            });
          }

          setUsers(allUsers);
        } else {
          setUsers([]);
        }
      } catch (error) {
        console.error("Error fetching developers:", error);
        setUsers([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (cardId) {
      fetchDevelopers();
    }
  }, [cardId]);

  if (isLoading || users.length === 0) {
    return null;
  }

  /** 🔹 OFFSETS (ONLY CHANGE) */
  const collapsedOffset = -16; // original stacked state
  const expandedOffset = 3; // hover spread (like before)

  return (
    <div
      className="relative flex items-center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setHoveredIndex(null);
      }}
    >
      {/* Avatar Group */}
      <div className="flex items-center">
        {/* Count Badge */}
        <div
          className="relative cursor-pointer"
          onMouseEnter={() => setHoveredIndex(-1)}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <div className="relative w-8 h-8">
            <AnimatePresence>
              {hoveredIndex === -1 && (
                <motion.span
                  layoutId="avatarHoverRing"
                  className="absolute -inset-[3px] rounded-full bg-neutral-200 pointer-events-none"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                />
              )}
            </AnimatePresence>

            {/* Counter avatar */}
            <div
              className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-[#1E1F26] text-white text-sm font-semibold transition-transform duration-300 ${
                hoveredIndex === -1
                  ? "scale-90 border-[2.5px] border-transparent"
                  : "border-[2.5px] border-white"
              }`}
            >
              {users.length}
            </div>
          </div>
        </div>

        {/* Users (Owner + Developers) */}
        {users.map((user, index) => {
          const initials = getInitials(user.name, user.email);
          const displayName = getDisplayName(user.name, user.email);
          const fullImageUrl = getFullImageUrl(user.profile_image);

          const marginLeft = isHovered ? expandedOffset : collapsedOffset;

          return (
            <div
              key={user.isOwner ? "owner" : user.email}
              className="relative transition-all duration-300 cursor-pointer"
              style={{
                marginLeft: `${marginLeft}px`,
                zIndex: hoveredIndex === index ? 50 : users.length - index,
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Fixed avatar wrapper */}
              <div className="relative w-8 h-8">
                <AnimatePresence>
                  {hoveredIndex === index && (
                    <motion.span
                      layoutId="avatarHoverRing"
                      className="absolute -inset-[3px] rounded-full bg-neutral-200 pointer-events-none"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                    />
                  )}
                </AnimatePresence>

                {/* Avatar */}
                <div
                  className={`relative z-10 w-full h-full rounded-full overflow-hidden flex items-center justify-center transition-colors duration-300 ${
                    isHovered
                      ? "border-[2.5px] border-transparent"
                      : "border-[2.5px] border-white"
                  }`}
                >
                  {fullImageUrl ? (
                    <img
                      src={fullImageUrl}
                      alt={displayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className={`w-full h-full ${
                        user.isOwner
                          ? "bg-gradient-to-br from-blue-400 to-indigo-500"
                          : "bg-gradient-to-br from-purple-400 to-pink-400"
                      } flex items-center justify-center text-white font-semibold text-xs`}
                    >
                      {initials}
                    </div>
                  )}
                </div>
              </div>

              {/* Name Tooltip */}
              {hoveredIndex === index && (
                <div className="absolute top-full left-0 mt-2 z-50 animate-tooltip-slide">
                  <div className="relative bg-white text-[#1E1F26] text-[18px] font-medium px-4 py-3 rounded-[10px] border border-[#E4E6EE] shadow whitespace-nowrap flex items-center gap-3">
                    <div className="w-[60px] h-[60px] rounded-[10px] overflow-hidden bg-white">
                      {fullImageUrl ? (
                        <img
                          src={fullImageUrl}
                          alt={displayName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div
                          className={`w-full h-full ${
                            user.isOwner
                              ? "bg-gradient-to-br from-blue-400 to-indigo-500"
                              : "bg-gradient-to-br from-purple-400 to-pink-400"
                          } flex items-center justify-center text-white font-semibold text-sm`}
                        >
                          {initials}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <span style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
                        {displayName}
                      </span>
                      <span
                        className="text-xs text-gray-500 font-normal"
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        {user.role}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes tooltip-in {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-tooltip-slide {
          animation: tooltip-in 0.15s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DeveloperAvatarsGroup;
